import { useEffect, useMemo } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import {
  applyAssetFeatureVisibility,
  applyAssetGraphicsVisibility,
} from '../EventsTab/assetMapSymbolVisibility';
import { assetSymbolTargetFor } from '../EventsTab/assetSymbolTargets';
import { useHiddenAssetSymbols } from '../EventsTab/useHiddenAssetSymbols';
import type { ArcgisMapView } from './utils/arcgisView';

/**
 * Keep every asset symbol on the map in line with the user's hidden list,
 * whichever tab is open. Re-applies when layers or their graphics change, since
 * demo and org layers redraw their pins.
 */
export function useAssetSymbolVisibilitySync(mapView: ArcgisMapView | null) {
  const { data: resourceSets } = useResourceSets();
  const { hiddenIds } = useHiddenAssetSymbols();

  const targets = useMemo(
    () =>
      (resourceSets ?? [])
        .filter((resourceSet) => !resourceSet.archived)
        .map(assetSymbolTargetFor),
    [resourceSets]
  );

  useEffect(() => {
    if (!mapView?.map) return;
    let cancelled = false;
    const apply = () => {
      if (cancelled) return;
      applyAssetGraphicsVisibility(mapView, targets, hiddenIds);
    };
    apply();
    void applyAssetFeatureVisibility(mapView, targets, hiddenIds).catch(
      () => undefined
    );

    const handles: Array<{ remove: () => void }> = [];
    const attach = () => {
      handles.splice(0).forEach((handle) => handle.remove());
      const visit = (layer: __esri.Layer) => {
        if (layer instanceof GraphicsLayer) {
          handles.push(layer.graphics.on('change', apply));
        }
        if (layer instanceof GroupLayer) {
          for (const child of layer.layers.toArray()) visit(child);
        }
      };
      (mapView.map?.layers.toArray() ?? []).forEach(visit);
      if (mapView.map) handles.push(mapView.map.allLayers.on('change', attach));
      apply();
    };
    attach();
    return () => {
      cancelled = true;
      handles.forEach((handle) => handle.remove());
    };
  }, [hiddenIds, mapView, targets]);
}
